import {HierarchyTableNode} from '@domain/expert-system'
import {ExpertSystemHierarchyNodeType, KpiCalculationError} from '@hconnect/common/types'
import {completionScoreBandColor} from '@hconnect/common/utils'
import {Tooltip} from '@hm/fundamental'
import {Box, Typography} from '@mui/material'
import {isNil} from 'lodash'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, VALIDATION_TRANSLATIONS_KEY} from '../consts'

const KPI_NODE_TYPES = new Set([
  ExpertSystemHierarchyNodeType.KilnKpi,
  ExpertSystemHierarchyNodeType.MillKpi
])

export const useRenderWeightedAverageCell = () => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const getWeightedAverageDisplayValue = useCallback(
    (item: HierarchyTableNode) => {
      if (isNil(item.weightedAverage) && !item.weightedAverageError) {
        return t('label.notApplicable')
      }
      if (item.weightedAverageError) {
        return item.weightedAverageError === KpiCalculationError.NoKpiValue
          ? '-'
          : t(VALIDATION_TRANSLATIONS_KEY[item.weightedAverageError])
      }
      if (item.weightedAverage !== undefined) return `${Math.round(item.weightedAverage)}%`
      return '-'
    },
    [t]
  )

  const getWeightedAverageColor = (item: HierarchyTableNode) => {
    if (isNil(item.weightedAverage) && !item.weightedAverageError) return 'text.disabled'
    return isNil(item.weightedAverage) ? 'initial' : completionScoreBandColor(item.weightedAverage)
  }

  const isPartialWeightedAverage = (item: HierarchyTableNode) =>
    item.weightedAverage !== undefined && item.weightedAverageIsPartial

  return useCallback(
    (item: HierarchyTableNode) => {
      if (!KPI_NODE_TYPES.has(item.type as ExpertSystemHierarchyNodeType)) return null

      const isPartial = isPartialWeightedAverage(item)
      const weightedAverageCell = (
        <Typography
          component="span"
          display="inline-flex"
          alignItems="center"
          variant="h4"
          sx={{color: getWeightedAverageColor(item)}}
          data-test-id={`expert-system-usage-weighted-average-${item.id}`}
        >
          {getWeightedAverageDisplayValue(item)}
          {isPartial && '*'}
        </Typography>
      )

      return (
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Box display="inline-flex" alignItems="center" gap={0.5}>
            {isPartial ? (
              <Tooltip content={t('label.partialYearTooltip')} maxWidth={320}>
                {weightedAverageCell}
              </Tooltip>
            ) : (
              weightedAverageCell
            )}
          </Box>
        </Box>
      )
    },
    [getWeightedAverageDisplayValue, t]
  )
}
